import { useState } from 'react';
import * as React from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import {
  AccountCircle,
  AdminPanelSettings,
  Notifications,
  SupportAgent,
} from '@mui/icons-material';
import ExitToApp from '@mui/icons-material/ExitToApp';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useGetAppVersionQuery } from '@/store/api/referenceApi';
import { useGetCurrentUserQuery } from '@/store/api/userApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setDarkMode } from '@/store/slices/commonSlice';
import {
  selectIsSuperUser,
  setSelectedCompany,
  setSelectedSide,
} from '@/store/slices/userSlice';

import { CompanyRole, RoutePath, Side } from '@/utils/enums';
import { getInitials } from '@/utils/helpers';

import { isLocalAuth, signOutLocal } from '../auth/localAuth';
import { LoadingIndicator } from '../common/Loading';
import { SideSelector } from './SideSelector';
import SuperUserCompanyMenu from './SuperUserCompanyMenu';
import UserCompanyMenu from './UserCompanyMenu';

import {
  avatarSx,
  companyRoleLabelSx,
  companySectionSx,
  formControlSx,
  iconSx,
  menuItemIconSx,
  menuItemSx,
  menuPaperSx,
  rolesContainerSx,
  sectionBoxSx,
  userAvatarContainerSx,
  userEmailSx,
  userInfoSectionSx,
  userNameSx,
  userWelcomeContainerSx,
} from './UserMenu.styles';

/** `v2.4.0-37-ge61870fb` → `2.4.0-37-ge61870fb`; `local` is shown as is. */
const toDisplayVersion = (version: string) => version.replace(/^v/, '');

export default function UserMenu() {
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const dispatch = useAppDispatch();

  const isUserSuperUser = useAppSelector(selectIsSuperUser);
  const userState = useAppSelector(state => state.user);
  const { selectedCompany, selectedSide } = userState;
  const selectedCompanyId = selectedCompany?.id || null;

  const commonState = useAppSelector(state => state.common);
  const { darkMode } = commonState;

  const { data: user, isLoading } = useGetCurrentUserQuery();
  const { data: appVersion } = useGetAppVersionQuery();

  // State for account menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (isLoading || !user) {
    return <LoadingIndicator />;
  }

  const userCompanies = user.companies || [];

  // Calculate current user roles based on selected company (only for regular users)
  const currentUserRoles =
    !isUserSuperUser && selectedCompanyId
      ? selectedCompany?.userRoles || []
      : [];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    if (isLocalAuth) {
      // Mock sign-in has no Auth0 session to end; back to the account picker
      signOutLocal();
      return;
    }
    logout({ logoutParams: { returnTo: globalThis.location.origin } });
  };

  const handleSettings = () => {
    navigate(RoutePath.SETTINGS_ACCOUNT);
    handleMenuClose();
  };

  const handleManageAlerts = () => {
    navigate(RoutePath.SETTINGS_ALERTS);
    handleMenuClose();
  };

  const handleDarkModeToggle = () => {
    // Save the new dark mode preference to session storage
    const newDarkMode = !darkMode;

    dispatch(setDarkMode(newDarkMode));
  };

  const handleSelectCompany = (companyId: string) => {
    const foundCompany = user.companies.find(
      company => company.id === companyId
    );

    if (foundCompany) {
      dispatch(setSelectedCompany(foundCompany));
    }
  };

  const handleSelectSide = (side: Side) => {
    dispatch(setSelectedSide(side));
  };

  return (
    <>
      {/* User information section with account menu */}
      <Box sx={userWelcomeContainerSx}>
        <Typography variant="body1" color="text.primary">
          Welcome, {user.firstName}
        </Typography>
        <Box
          sx={userAvatarContainerSx}
          onClick={handleMenuOpen}
          id="userAvatar"
        >
          <Tooltip title="Account Settings">
            <IconButton aria-label="settings">
              <Avatar sx={avatarSx(darkMode)}>{getInitials(user)}</Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Account Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: menuPaperSx,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        id="AccountMenu"
      >
        {/* User Info Section */}
        <Box sx={userInfoSectionSx}>
          <Typography variant="h6" sx={userNameSx}>
            {user.firstName} {user.lastName}
            {user.isPlatformAdmin && (
              <Tooltip title="Platform Admin">
                <AdminPanelSettings color="primary" sx={iconSx} />
              </Tooltip>
            )}
            {user.isTechnicalSupport && (
              <Tooltip title="Technical Support">
                <SupportAgent color="primary" sx={iconSx} />
              </Tooltip>
            )}
          </Typography>
          <Typography variant="body2" sx={userEmailSx}>
            {user.email}
          </Typography>
        </Box>

        {/* Current Company Section */}
        <Box sx={companySectionSx}>
          {isUserSuperUser ? (
            <SuperUserCompanyMenu
              selectedCompanyId={selectedCompanyId}
              onCompanyChange={handleSelectCompany}
              userCompanies={userCompanies}
            />
          ) : (
            <UserCompanyMenu
              selectedCompanyId={selectedCompanyId}
              onCompanyChange={handleSelectCompany}
              userCompanies={userCompanies}
            />
          )}
        </Box>

        {/* Side Selector Section */}
        {selectedCompany?.companyRole === CompanyRole.BI_DIRECTIONAL && (
          <SideSelector
            selectedSide={selectedSide}
            selectedCompany={selectedCompany}
            onSideChange={handleSelectSide}
          />
        )}

        {/* Company Role Section */}
        {!isUserSuperUser && (
          <Box sx={companySectionSx}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={companyRoleLabelSx}
            >
              Company Role
            </Typography>
            <Box sx={rolesContainerSx}>
              {currentUserRoles.length > 0 ? (
                currentUserRoles.map(role => (
                  <Chip
                    key={role.id}
                    label={role.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No roles assigned
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Dark Mode Toggle */}
        <Box sx={sectionBoxSx}>
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={handleDarkModeToggle}
                size="small"
              />
            }
            label={darkMode ? 'Dark Mode' : 'Light Mode'}
            labelPlacement="end"
            sx={formControlSx}
          />
        </Box>

        {/* Action Links */}
        <MenuItem onClick={handleManageAlerts} sx={menuItemSx}>
          <ListItemText primary="Manage Alerts" />
          <Notifications sx={menuItemIconSx} />
        </MenuItem>

        <MenuItem onClick={handleSettings} sx={menuItemSx}>
          <ListItemText primary="Account Settings" />
          <AccountCircle sx={menuItemIconSx} />
        </MenuItem>

        <Divider />

        {/* Version Info: the deployed backend's version; hidden until it loads or if it fails */}
        {appVersion && (
          <Box sx={sectionBoxSx}>
            <Typography variant="caption" color="text.secondary">
              CleanTrade Version: {toDisplayVersion(appVersion)}
            </Typography>
          </Box>
        )}

        {/*Logout Button*/}
        <MenuItem onClick={handleLogout} sx={menuItemSx}>
          <ListItemText primary="Logout" />
          <IconButton aria-label="logout">
            <ExitToApp />
          </IconButton>
        </MenuItem>
      </Menu>
    </>
  );
}
